/**
 * Login Page - Willbanks Operations Hub
 */

import { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../shared/contexts/AuthContext';
import axios from 'axios';

type DemoRole = 'admin' | 'operator' | 'viewer';
type AuthProvider = 'demo' | 'azure-ad';

interface DemoCredentials {
  email: string;
  password: string;
}

interface LocationState {
  from?: {
    pathname: string;
  };
}

const loaderBg = 'radial-gradient(circle at top left, rgba(37,99,235,0.18), transparent 55%), radial-gradient(circle at 20% 85%, rgba(14,116,144,0.16), transparent 48%), linear-gradient(135deg, #f8fafc 0%, #eef2f7 40%, #f5f7fb 100%)';

export default function Login(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authProvider, setAuthProvider] = useState<AuthProvider>('demo');
  const [checkingProvider, setCheckingProvider] = useState(true);

  const from = (location.state as LocationState)?.from?.pathname || '/hub/truck-router/create-transport';

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      const params = new URLSearchParams(hash);
      const oauthError = params.get('error');
      const oauthMessage = params.get('message');
      if (oauthError || oauthMessage) {
        setError(oauthMessage || oauthError || 'Authentication failed');
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, []);

  useEffect(() => {
    const checkAuthProvider = async () => {
      try {
        const response = await axios.get('/api/auth/provider');
        setAuthProvider(response.data.provider || 'demo');
      } catch {
        setAuthProvider('demo');
      } finally {
        setCheckingProvider(false);
      }
    };
    checkAuthProvider();
  }, []);

  const handleAzureAdLogin = async () => {
    try {
      setError('');
      const response = await axios.get('/api/auth/oauth/url');
      if (response.data.authUrl) {
        window.location.href = response.data.authUrl;
      } else {
        setError('Failed to get login URL');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to initiate login');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    const result = await login(formData.email, formData.password);
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error || 'Login failed');
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const fillDemoCredentials = (role: DemoRole): void => {
    const credentials: Record<DemoRole, DemoCredentials> = {
      admin: { email: 'admin@demo.com', password: 'demo123' },
      operator: { email: 'operator@demo.com', password: 'demo123' },
      viewer: { email: 'viewer@demo.com', password: 'demo123' }
    };
    setFormData(credentials[role]);
    setError('');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: loaderBg }}
    >
      <div className="max-w-md w-full relative z-10">
        <div className="flex flex-col items-center space-y-8">
          {/* Branding */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <img
                src="/willbanks-logo.png"
                alt="Willbanks"
                className="h-16 w-auto"
              />
            </div>
            <h1 className="text-3xl tablet:text-4xl font-bold text-slate-900 font-heading tracking-tight">
              Transport Management
            </h1>
          </div>

          {/* Login Form */}
          <div className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-6 tablet:p-8">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900 font-heading mb-1">Welcome Back</h2>
              <p className="text-sm text-slate-500">Sign in to access your operations hubs</p>
            </div>

            {checkingProvider ? (
              <div className="flex items-center justify-center py-8">
                <svg className="animate-spin h-8 w-8 text-slate-800" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : authProvider === 'azure-ad' ? (
              <div className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-red-700 font-medium">{error}</p>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleAzureAdLogin}
                  disabled={loading}
                  className="w-full bg-slate-800 text-white py-3.5 px-4 rounded-xl font-semibold hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="10" height="10" fill="#f25022"/>
                    <rect x="11" width="10" height="10" fill="#7fba00"/>
                    <rect y="11" width="10" height="10" fill="#00a4ef"/>
                    <rect x="11" y="11" width="10" height="10" fill="#ffb900"/>
                  </svg>
                  {loading ? 'Redirecting...' : 'Sign in with Microsoft'}
                </button>

                <p className="text-xs text-slate-500 text-center">
                  You will be redirected to Microsoft to sign in with your work account
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="flex">
                        <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-red-700 font-medium">{error}</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 outline-none transition-all bg-white text-sm"
                      placeholder="you@willbanksmetals.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        required
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-400 focus:border-slate-400 outline-none transition-all bg-white text-sm"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPassword ? (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-slate-800 text-white py-3 px-4 rounded-xl font-semibold hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>

                {/* Demo Credentials */}
                <div className="pt-5 border-t border-slate-100">
                  <p className="text-xs text-slate-500 text-center mb-3">Demo Quick Access</p>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => fillDemoCredentials('admin')}
                      className="px-3 py-2.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors text-center"
                    >
                      Admin
                    </button>
                    <button
                      type="button"
                      onClick={() => fillDemoCredentials('operator')}
                      className="px-3 py-2.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors text-center"
                    >
                      Operator
                    </button>
                    <button
                      type="button"
                      onClick={() => fillDemoCredentials('viewer')}
                      className="px-3 py-2.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors text-center"
                    >
                      Viewer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-[10px] text-slate-400">
              © {new Date().getFullYear()} Willbanks Metals • Transport Management v1.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
